import { Box, Button, Stack, ThemeSpaceVariable } from "@caspeco/casper-ui-library";
import {
    useAvailableTimes,
    useFilteredAvailableTimes,
    useFilteredExperiences,
} from "api/api-hooks/use-available-times";
import {
    getAvailableExperienceIds,
    getAvailableSectionIds,
    useDayStates,
} from "api/api-hooks/use-day-states";
import { useExperienceView } from "api/api-hooks/use-experience-view";
import { useExternalBookingSettings } from "api/api-hooks/use-external-booking-settings";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import { ActivityModal } from "components/booking-modal/activity-modal";
import { ExperienceModal } from "components/booking-modal/experience-modal";
import SomethingWrong from "components/error-components/something-wrong";
import { ExperienceViewContent } from "components/pages/experience-view/experience-view-modes";
import { deriveExperienceViewState } from "components/pages/experience-view/experience-view-utils";
import { IntroductionMessage } from "components/pages/experience-view/experience-view-parts";
import SummaryAside from "components/selection-components/summary/summary-aside";
import { useBookingStateContext } from "hooks/use-booking-state";
import { useMissingMenuSelection } from "hooks/use-missing-menu-selection";
import { PageType } from "logger/page-tracking";
import { usePageTracking } from "logger/use-page-tracking";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Routes } from "routes";
import { UrlParamName } from "search-params";
import { AvailableTimesSection } from "types/available-times";
import { Experience } from "types/experience";
import { isExperienceItem } from "types/experience-view";
import type {
    ExperienceViewExperienceItem,
    ExperienceViewSectionItem,
} from "types/experience-view";

function ExperienceView() {
    const { t } = useTranslation();
    const { data: items, isLoading, isError } = useExperienceView();
    const { date, isActivityBooking, guests, isWaitList } = useBookingStateContext();
    const [openActivity, setOpenActivity] = useState<AvailableTimesSection | null>(null);
    const [openActivityIsWebRule, setOpenActivityIsWebRule] = useState(false);
    const { isLoading: isLoadingAvailableTimes } = useAvailableTimes(date);
    const filteredAvailableTimes = useFilteredAvailableTimes(date);
    const filteredExperiences = useFilteredExperiences(date);
    const { data: booking } = useWebBooking();
    const { data: settings } = useExternalBookingSettings();
    const { data: dayStates } = useDayStates(date?.toDate() ?? new Date());
    const { sectionIdsFromDayStates, experienceIdsFromDayStates } = useMemo(() => {
        if (!date || !dayStates)
            return { sectionIdsFromDayStates: null, experienceIdsFromDayStates: null };
        const dateKey = date.format("YYYY-MM-DD");
        const g = guests ?? 0;
        return {
            sectionIdsFromDayStates: getAvailableSectionIds(dayStates, dateKey, g),
            experienceIdsFromDayStates: getAvailableExperienceIds(dayStates, dateKey, g),
        };
    }, [date, dayStates, guests]);
    // The available-times query is keyed on date and guests, so changing either leaves the listing
    // with nothing to filter by until it resolves. Day states are a different answer rather than an
    // older one — they ignore waitlist and know nothing about chains — so the previous answer is
    // kept instead, snapshotted with the guest count it was for.
    const lastResolvedAvailability = useRef<{
        filteredAvailableTimes: AvailableTimesSection[];
        filteredExperiences: Experience[];
        guests: number | null | undefined;
    } | null>(null);
    const [openExperience, setOpenExperience] = useState<Experience | null>(null);
    // The experience a suggestion was followed from, so following one is not a one-way door.
    const [suggestionOrigin, setSuggestionOrigin] = useState<Experience | null>(null);
    const [openOnMenuStep, setOpenOnMenuStep] = useState(false);
    const ruleIdMissingMenuSelection = useMissingMenuSelection();
    const navigate = useNavigate();
    const location = useLocation();

    usePageTracking({
        pageName: "ExperienceView",
        pageType: PageType.Page,
        metadata: {
            guests: guests ?? 1,
            hasDate: !!date,
            isActivityBooking: !!isActivityBooking,
            totalItems: items?.length ?? 0,
        },
        autoTrack: !isLoading,
        dependencies: [isLoading, items?.length],
    });

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const isExperienceModeParam = searchParams.get(UrlParamName.IS_EXPERIENCE_VIEW_MODE);
        if (isExperienceModeParam !== "true") {
            searchParams.set(UrlParamName.IS_EXPERIENCE_VIEW_MODE, "true");
            void navigate(
                {
                    pathname: location.pathname,
                    search: searchParams.toString(),
                },
                { replace: true }
            );
        }
    }, [location.pathname, location.search, navigate]);

    if (isLoading || !items) return null;
    if (isError) return <SomethingWrong />;

    if (!isLoadingAvailableTimes) {
        lastResolvedAvailability.current = {
            filteredAvailableTimes,
            filteredExperiences,
            guests,
        };
    }
    const previousAvailability = isLoadingAvailableTimes ? lastResolvedAvailability.current : null;

    // Read everywhere on the page, so what the guest can open is the availability they can see.
    const availableExperiences = previousAvailability?.filteredExperiences ?? filteredExperiences;
    const availableTimes = previousAvailability?.filteredAvailableTimes ?? filteredAvailableTimes;

    const state = deriveExperienceViewState({
        items,
        date,
        filteredExperiences: availableExperiences,
        filteredAvailableTimes: availableTimes,
        booking,
        // Leaves day states standing in only for the first load, where there is no previous answer.
        isLoadingAvailableTimes: isLoadingAvailableTimes && !previousAvailability,
        sectionIdsFromDayStates,
        experienceIdsFromDayStates,
        guests: previousAvailability ? previousAvailability.guests : guests,
    });
    const { hasBookedItems } = state;

    const handleExperienceClick = (experienceViewItem: ExperienceViewExperienceItem) => {
        const experienceWithTimes = availableExperiences.find(
            (exp) => exp.id === experienceViewItem.id
        );

        const base = experienceWithTimes ?? new Experience({ id: experienceViewItem.id });
        const finalExperience = new Experience({
            ...base,
            name: experienceViewItem.name,
            description: experienceViewItem.description,
            imageReference1x1: experienceViewItem.imageReference1x1 || base.imageReference1x1,
            imageReference16x9: experienceViewItem.imageReference16x9 || base.imageReference16x9,
            // Before a date is chosen there are no times to read it from.
            informationOnly:
                experienceWithTimes?.informationOnly ?? experienceViewItem.informationOnly,
        });

        setOpenExperience(finalExperience);
    };

    const handleSectionClick = (section: ExperienceViewSectionItem) => {
        const sectionWithTimes = availableTimes.find((s) => s.id === section.id);

        const base = sectionWithTimes ?? new AvailableTimesSection({ id: section.id });
        const finalSection = {
            ...base,
            name: section.name,
            description: section.description,
            image: section.imageReference16x9,
        } as unknown as AvailableTimesSection;

        setOpenActivity(finalSection);
    };

    // Adapters for SummaryAside which identifies activities by ruleId (timeSet.id)
    const handleOpenActivityById = (ruleId: number) => {
        const section = availableTimes.find((s) => s.timeSets.some((ts) => ts.id === ruleId));
        if (section) setOpenActivity(section);
    };

    const handleOpenExperienceById = (experienceId: number) => {
        const item = items.find(
            (i): i is ExperienceViewExperienceItem => isExperienceItem(i) && i.id === experienceId
        );
        if (item) handleExperienceClick(item);
    };

    const openWebRuleAsActivity = (webRuleAsSection: AvailableTimesSection) => {
        setOpenActivity(webRuleAsSection);
        setOpenActivityIsWebRule(true);
    };

    /**
     * Reopens whatever owns the unanswered menu — a plain section or an experience — on its menu
     * step. Returns false when neither can be found, so the caller can decide what to do instead.
     */
    const openMenuStepFor = (ruleId: number): boolean => {
        const section = availableTimes.find((s) => s.timeSets.some((ts) => ts.id === ruleId));
        if (section) {
            setOpenOnMenuStep(true);
            setOpenActivity(section);
            return true;
        }

        const experienceId = booking?.webBookingRules?.find((r) => r.id === ruleId)?.experienceId;
        const experienceItem = experienceId
            ? items.find(
                  (i): i is ExperienceViewExperienceItem =>
                      isExperienceItem(i) && i.id === experienceId
              )
            : undefined;
        if (experienceItem) {
            setOpenOnMenuStep(true);
            handleExperienceClick(experienceItem);
            return true;
        }

        return false;
    };

    const goToDetailsForm = () => {
        const pathname = window.location.pathname.replace(Routes.EXPERIENCE_VIEW, Routes.FINALIZE);
        // Replace so that experience-view/:guid is not kept in history for simple bookings — Back will then return
        // to experience-view without a GUID, ensuring the booking is cleared on back navigation.
        void navigate(
            { pathname, search: location.search },
            { replace: !isActivityBooking || !!isWaitList }
        );
    };

    /**
     * The guest asking to move on. A booked activity can require a menu selection that has not been
     * made yet, in which case that is reopened on its menu step instead. If it cannot be reopened —
     * the section or experience is no longer listed for the selected date — fall through rather than
     * leaving the guest on a dead button.
     *
     * Only for the guest's own "proceed" action. The modals navigate via goToDetailsForm directly:
     * they have just finished their menu step, and their callback captures a value from before it was
     * answered, which would bounce the guest straight back into the menu.
     */
    const proceedToDetailsForm = () => {
        if (
            ruleIdMissingMenuSelection !== undefined &&
            openMenuStepFor(ruleIdMissingMenuSelection)
        ) {
            return;
        }

        goToDetailsForm();
    };

    const rules = booking?.webBookingRules ?? [];
    const uniqueNormalRuleIds = new Set(rules.filter((r) => !r.experienceId).map((r) => r.id));
    const uniqueExperienceIds = new Set(
        rules.filter((r) => r.experienceId).map((r) => r.experienceId)
    );
    const bookedActivitiesCount = uniqueNormalRuleIds.size + uniqueExperienceIds.size;

    const footerHeight = "88px"; // Height of the fixed footer with the continue button on mobile/tablet
    return (
        <Stack
            mt={ThemeSpaceVariable.XSmall}
            px={ThemeSpaceVariable.Medium}
            spacing={ThemeSpaceVariable.None}
            pb={
                hasBookedItems
                    ? { base: footerHeight, lg: ThemeSpaceVariable.None }
                    : ThemeSpaceVariable.None
            }
        >
            {!date && (
                <IntroductionMessage
                    header={settings?.introductionMessageHeader}
                    message={settings?.introductionMessage}
                    template={settings?.messageTemplates?.introductionMessage}
                />
            )}
            <ExperienceViewContent
                state={state}
                isActivityBooking={!!isActivityBooking}
                isLoadingAvailableTimes={isLoadingAvailableTimes}
                onExperienceClick={handleExperienceClick}
                onSectionClick={handleSectionClick}
                onWebRuleClick={openWebRuleAsActivity}
            />

            <ActivityModal
                section={openActivity}
                setSummaryOpen={() => {}}
                onClose={() => {
                    setOpenActivity(null);
                    setOpenActivityIsWebRule(false);
                    setOpenOnMenuStep(false);
                }}
                onConfirm={!isActivityBooking || isWaitList ? goToDetailsForm : undefined}
                isWebRule={openActivityIsWebRule}
                openOnMenuStep={openOnMenuStep}
            />
            <ExperienceModal
                experience={openExperience}
                onClose={() => {
                    setOpenExperience(null);
                    setSuggestionOrigin(null);
                    setOpenOnMenuStep(false);
                }}
                onConfirm={!isActivityBooking || isWaitList ? goToDetailsForm : undefined}
                openOnMenuStep={openOnMenuStep}
                onSelectExperience={(experienceId) => {
                    setSuggestionOrigin(openExperience);
                    handleOpenExperienceById(experienceId);
                }}
                onReturnToOrigin={
                    suggestionOrigin
                        ? () => {
                              setOpenExperience(suggestionOrigin);
                              setSuggestionOrigin(null);
                          }
                        : undefined
                }
            />

            {/* Desktop: booking overview in the aside panel */}
            {hasBookedItems && (
                <SummaryAside
                    handleOpenActivity={handleOpenActivityById}
                    handleOpenExperience={handleOpenExperienceById}
                    gotoDetailsForm={proceedToDetailsForm}
                />
            )}

            {/* Mobile/tablet: sticky bottom button to proceed to details & payment */}
            {hasBookedItems && (
                <Box
                    display={{ base: "block", lg: "none" }}
                    position="fixed"
                    bottom={0}
                    left={0}
                    right={0}
                    p={ThemeSpaceVariable.Medium}
                    zIndex={1}
                >
                    <Button
                        data-testid="continue-btn"
                        size="xl"
                        w="full"
                        onClick={proceedToDetailsForm}
                        variant="primary"
                        p={ThemeSpaceVariable.Small}
                        justifyContent="center"
                        textAlign="center"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                        display="block"
                    >
                        {t("reviewBooking") + ` (${bookedActivitiesCount})`}
                    </Button>
                </Box>
            )}
        </Stack>
    );
}

export default ExperienceView;
